import type { DependencyEdgeType, ProjectTask, TaskDependencyEdge } from '../types';
import { differenceInCalendarDays } from 'date-fns';
import { getDependencyEdges, taskIdsInUndirectedDependencyComponent } from './taskDependencies';
import {
  fmtDay,
  ganttScheduleDebugGroup,
  ganttScheduleDebugLog,
  isGanttScheduleDebugEnabled,
} from './ganttScheduleDebug';
import { formatGanttTaskIdForLog } from './ganttTaskIds';

export function stripTime(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Same calendar day in local time (after normalizing both to local midnight). */
export function sameLocalCalendarDay(a: Date, b: Date): boolean {
  return stripTime(a).getTime() === stripTime(b).getTime();
}

/** `YYYY-MM-DD` for `<input type="date">` using the local calendar (not UTC). */
export function toLocalDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Human-friendly due label: Today / Tomorrow / Yesterday from **local calendar** days
 * (not raw 24h deltas), so date-only tasks match the due date field and scheduling `stripTime` semantics.
 */
export function formatRelativeLocalCalendarDay(d: Date, now: Date = new Date()): string {
  const diff = differenceInCalendarDays(stripTime(d), stripTime(now));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** True when the due calendar day (local) is strictly before today — date-only overdue, not "before this instant". */
export function isTaskDueDatePastLocalCalendar(due: Date, now: Date = new Date()): boolean {
  return differenceInCalendarDays(stripTime(due), stripTime(now)) < 0;
}

export function isWeekend(d: Date): boolean {
  const day = stripTime(d).getDay();
  return day === 0 || day === 6;
}

export function snapStartToBusinessDay(d: Date): Date {
  let x = stripTime(d);
  while (isWeekend(x)) {
    x.setDate(x.getDate() + 1);
  }
  return x;
}

export function snapEndToBusinessDay(d: Date): Date {
  let x = stripTime(d);
  while (isWeekend(x)) {
    x.setDate(x.getDate() - 1);
  }
  return x;
}

/**
 * First business day strictly after `endDate` (calendar day after pred end).
 * FS lag 0 does **not** use this — see {@link earliestSuccessorStart} (same-day start).
 */
export function firstBusinessDayStrictlyAfter(endDate: Date): Date {
  const x = stripTime(endDate);
  x.setDate(x.getDate() + 1);
  return snapStartToBusinessDay(x);
}

/** Calendar day of predecessor finish, snapped to a business-day start (weekends roll forward). */
function fsEarliestBaseSameDayAsPredEnd(predEnd: Date): Date {
  return snapStartToBusinessDay(stripTime(predEnd));
}

/** Move forward `n` business days from `start` (n>=0). `start` is snapped first. */
export function addBusinessDaysFromStart(start: Date, n: number): Date {
  if (n <= 0) return snapStartToBusinessDay(start);
  let x = snapStartToBusinessDay(start);
  let left = n;
  while (left > 0) {
    x.setDate(x.getDate() + 1);
    if (!isWeekend(x)) left--;
  }
  return x;
}

/** Move backward `n` business days from `d` (for negative FS lag / overlap). */
function subtractBusinessDaysBefore(d: Date, n: number): Date {
  if (n <= 0) return snapStartToBusinessDay(d);
  let x = snapStartToBusinessDay(new Date(d));
  let left = n;
  while (left > 0) {
    x.setDate(x.getDate() - 1);
    if (!isWeekend(x)) left--;
  }
  return stripTime(x);
}

/**
 * Earliest allowed successor start for FS + lag.
 * - **Lag 0:** same calendar day as predecessor end (that day as a business-day start; weekends snap forward).
 * - **Lag &gt; 0:** that many **additional** business days after the lag-0 day.
 * - **Negative lag:** overlap / lead (business days before the lag-0 day).
 */
export function earliestSuccessorStart(predEnd: Date, lagBusinessDays: number): Date {
  const base0 = fsEarliestBaseSameDayAsPredEnd(predEnd);
  const lag = Math.floor(Number(lagBusinessDays) || 0);
  if (lag === 0) return base0;
  if (lag > 0) return addBusinessDaysFromStart(base0, lag);
  return subtractBusinessDaysBefore(base0, -lag);
}

/**
 * FS lag (weekdays) implied by actual dates: inverse of {@link earliestSuccessorStart} for this pair.
 * Use when displaying lag so the value matches the bars (stored lag can drift after manual moves).
 */
export function inferFsLagBusinessDays(predEnd: Date, succStart: Date): number {
  const base0 = fsEarliestBaseSameDayAsPredEnd(predEnd);
  const s = snapStartToBusinessDay(succStart);
  const bt = stripTime(base0).getTime();
  const st = stripTime(s).getTime();
  if (st === bt) return 0;
  if (st > bt) {
    let k = 0;
    let cur = new Date(base0);
    while (stripTime(cur).getTime() < st) {
      cur.setDate(cur.getDate() + 1);
      if (!isWeekend(cur)) k++;
    }
    return k;
  }
  let n = 0;
  const x = new Date(base0);
  while (stripTime(x).getTime() > st) {
    x.setDate(x.getDate() - 1);
    if (!isWeekend(x)) n++;
  }
  return -n;
}

/**
 * Earliest allowed successor start for SS + lag (weekdays after predecessor **start**).
 */
export function earliestSuccessorStartSs(predStart: Date, lagBusinessDays: number): Date {
  const base = snapStartToBusinessDay(predStart);
  const lag = Math.floor(Number(lagBusinessDays) || 0);
  if (lag === 0) return base;
  if (lag > 0) return addBusinessDaysFromStart(base, lag);
  return subtractBusinessDaysBefore(base, -lag);
}

/** SS lag (weekdays) implied by pred/succ starts — inverse of {@link earliestSuccessorStartSs}. */
export function inferSsLagBusinessDays(predStart: Date, succStart: Date): number {
  const p = snapStartToBusinessDay(predStart);
  const s = snapStartToBusinessDay(succStart);
  const pt = stripTime(p).getTime();
  const st = stripTime(s).getTime();
  if (st === pt) return 0;
  if (st > pt) {
    let lag = 0;
    const x = new Date(p);
    while (stripTime(x).getTime() < st) {
      x.setDate(x.getDate() + 1);
      if (!isWeekend(x)) lag++;
    }
    return lag;
  }
  let n = 0;
  const x = new Date(p);
  while (stripTime(x).getTime() > st) {
    x.setDate(x.getDate() - 1);
    if (!isWeekend(x)) n++;
  }
  return -n;
}

/** Earliest allowed successor finish for FF + lag (weekdays from predecessor finish). */
export function earliestSuccessorFinishFf(predEnd: Date, lagBusinessDays: number): Date {
  const base0 = stripTime(snapEndToBusinessDay(predEnd));
  const lag = Math.floor(Number(lagBusinessDays) || 0);
  if (lag === 0) return base0;
  if (lag > 0) return addBusinessDaysFromStart(base0, lag);
  return subtractBusinessDaysBefore(base0, -lag);
}

/** FF lag (weekdays) implied by pred/succ finishes. */
export function inferFfLagBusinessDays(predEnd: Date, succEnd: Date): number {
  const base0 = stripTime(snapEndToBusinessDay(predEnd));
  const s = stripTime(snapEndToBusinessDay(succEnd));
  const bt = base0.getTime();
  const st = s.getTime();
  if (st === bt) return 0;
  if (st > bt) {
    let k = 0;
    const cur = new Date(base0);
    while (stripTime(cur).getTime() < st) {
      cur.setDate(cur.getDate() + 1);
      if (!isWeekend(cur)) k++;
    }
    return k;
  }
  let n = 0;
  const x = new Date(base0);
  while (stripTime(x).getTime() > st) {
    x.setDate(x.getDate() - 1);
    if (!isWeekend(x)) n++;
  }
  return -n;
}

/** Earliest allowed successor finish for SF + lag (weekdays from predecessor start). */
export function earliestSuccessorFinishSf(predStart: Date, lagBusinessDays: number): Date {
  const base0 = stripTime(snapEndToBusinessDay(predStart));
  const lag = Math.floor(Number(lagBusinessDays) || 0);
  if (lag === 0) return base0;
  if (lag > 0) return addBusinessDaysFromStart(base0, lag);
  return subtractBusinessDaysBefore(base0, -lag);
}

/** SF lag (weekdays) implied by predecessor start vs successor finish. */
export function inferSfLagBusinessDays(predStart: Date, succEnd: Date): number {
  const base0 = stripTime(snapEndToBusinessDay(predStart));
  const s = stripTime(snapEndToBusinessDay(succEnd));
  const bt = base0.getTime();
  const st = s.getTime();
  if (st === bt) return 0;
  if (st > bt) {
    let lag = 0;
    const x = new Date(base0);
    while (stripTime(x).getTime() < st) {
      x.setDate(x.getDate() + 1);
      if (!isWeekend(x)) lag++;
    }
    return lag;
  }
  let n = 0;
  const x = new Date(base0);
  while (stripTime(x).getTime() > st) {
    x.setDate(x.getDate() - 1);
    if (!isWeekend(x)) n++;
  }
  return -n;
}

/**
 * When creating a dependency: if **business-day** implied lag (Mon–Fri count, not calendar days) is strictly below this, keep task
 * dates and persist that lag; otherwise persist {@link CREATE_DEP_LARGE_GAP_LAG_BUSINESS_DAYS} and enforce schedule.
 */
export const CREATE_DEP_IMPLIED_LAG_MOVE_THRESHOLD = 10;
/** Stored lag for the “large gap” branch — always business days (weekdays). */
export const CREATE_DEP_LARGE_GAP_LAG_BUSINESS_DAYS = 1;

/** FS/SS/FF/SF lag in **business days** implied by bar dates — not calendar-day distance. */
export function impliedLagForNewDependencyEdge(
  pred: ProjectTask,
  succ: ProjectTask,
  linkType: DependencyEdgeType,
): number {
  const pe = new Date(pred.endDate);
  const ps = new Date(pred.startDate);
  const se = new Date(succ.endDate);
  const ss = new Date(succ.startDate);
  let raw: number;
  switch (linkType) {
    case 'SS':
      raw = inferSsLagBusinessDays(ps, ss);
      break;
    case 'FF':
      raw = inferFfLagBusinessDays(pe, se);
      break;
    case 'SF':
      raw = inferSfLagBusinessDays(ps, se);
      break;
    default:
      raw = inferFsLagBusinessDays(pe, ss);
      break;
  }
  return Math.max(-500, Math.min(500, Math.floor(Number(raw) || 0)));
}

export function countBusinessDaysInclusive(start: Date, end: Date): number {
  const s = stripTime(start);
  const e = stripTime(end);
  if (e < s) return 1;
  let c = 0;
  const d = new Date(s);
  while (d <= e) {
    if (!isWeekend(d)) c++;
    d.setDate(d.getDate() + 1);
  }
  return Math.max(1, c);
}

export type BusinessWeekdaysRemaining =
  | { kind: 'invalid' }
  | { kind: 'past' }
  | { kind: 'dueToday' }
  | { kind: 'remaining'; count: number };

/**
 * Weekdays strictly after **today's local calendar day** through **due** (inclusive).
 * Due today → 0; due before today → past; excludes today from the count so it reads as "full weekdays still ahead".
 */
export function businessWeekdaysRemainingUntilDue(due: Date, now: Date = new Date()): BusinessWeekdaysRemaining {
  const t0 = stripTime(now);
  const t1 = stripTime(due);
  if (Number.isNaN(t1.getTime())) return { kind: 'invalid' };
  if (t1 < t0) return { kind: 'past' };
  if (t1.getTime() === t0.getTime()) return { kind: 'dueToday' };
  let d = new Date(t0);
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  let count = 0;
  while (d <= t1) {
    if (!isWeekend(d)) count++;
    d.setDate(d.getDate() + 1);
  }
  return { kind: 'remaining', count };
}

export function formatBusinessWeekdaysRemainingLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '—';
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return '—';
  const r = businessWeekdaysRemainingUntilDue(due, now);
  switch (r.kind) {
    case 'invalid':
      return '—';
    case 'past':
      return 'Past due';
    case 'dueToday':
      return 'Due today';
    case 'remaining':
      return r.count === 1 ? '1 weekday left' : `${r.count} weekdays left`;
    default:
      return '—';
  }
}

export function endDateFromStartAndInclusiveBizDays(start: Date, inclusiveBizDays: number): Date {
  const n = Math.max(1, inclusiveBizDays);
  let end = snapStartToBusinessDay(start);
  let count = 1;
  while (count < n) {
    end.setDate(end.getDate() + 1);
    if (!isWeekend(end)) count++;
  }
  return stripTime(end);
}

/** Inclusive business-day span — inverse of {@link endDateFromStartAndInclusiveBizDays} for a snapped end. */
export function startDateFromEndAndInclusiveBizDays(end: Date, inclusiveBizDays: number): Date {
  const n = Math.max(1, inclusiveBizDays);
  let start = stripTime(snapEndToBusinessDay(end));
  let count = 1;
  while (count < n) {
    start.setDate(start.getDate() - 1);
    if (!isWeekend(start)) count++;
  }
  return stripTime(snapStartToBusinessDay(start));
}

export interface TaskDateState {
  startDate: Date;
  endDate: Date;
}

/**
 * Reported by the scheduler when a locked task could not be moved to its required start.
 * The UI surfaces these as warning toasts so the PM knows links are broken until they intervene.
 */
export interface ScheduleLockViolation {
  id: string;
  /**
   * Why the solver could not shift this successor: completions pin dates; explicit {@link ProjectTask.scheduleLocked} pins independently.
   * Omitted for backward compatibility — UI may infer from scheduling nodes.
   */
  pinnedReason?: 'completed' | 'explicit';
  /** Required when start constraint is violated. */
  requiredStart?: Date;
  currentStart?: Date;
  /** Required when finish constraint (FF/SF) is violated. */
  requiredEnd?: Date;
  currentEnd?: Date;
}

/** Mirrors {@link isTaskLocked} causality for clearer UI copy. */
function lockViolationPinnedReason(t: ProjectTask): 'completed' | 'explicit' {
  return t.status === 'completed' ? 'completed' : 'explicit';
}

export interface ScheduleResult {
  dates: Map<string, TaskDateState>;
  error?: string;
  violations?: ScheduleLockViolation[];
}

/**
 * Hard-pin check: locked tasks (or completed tasks, which auto-lock) are immovable
 * by every automated scheduler path. Direct user drags of a locked bar are also disabled
 * upstream in the Gantt UI.
 */
export function isTaskLocked(t: ProjectTask | undefined | null): boolean {
  if (!t) return false;
  return t.scheduleLocked === true || t.status === 'completed';
}

function buildStateMap(tasks: ProjectTask[]): Map<string, TaskDateState> {
  const m = new Map<string, TaskDateState>();
  for (const t of tasks) {
    if (!t.id) continue;
    m.set(t.id, {
      startDate: snapStartToBusinessDay(new Date(t.startDate)),
      endDate: snapEndToBusinessDay(new Date(t.endDate)),
    });
  }
  return m;
}

function topologicalOrder(tasks: ProjectTask[]): { ok: boolean; order?: string[]; error?: string } {
  const ids = tasks.map((t) => t.id!).filter(Boolean);
  if (ids.length === 0) return { ok: true, order: [] };

  const inDegree = new Map<string, number>();
  const successors = new Map<string, string[]>();
  ids.forEach((id) => {
    inDegree.set(id, 0);
    successors.set(id, []);
  });

  for (const t of tasks) {
    if (!t.id) continue;
    const edges = getDependencyEdges(t).filter((e) => ids.includes(e.predecessorId));
    inDegree.set(t.id, edges.length);
    for (const e of edges) {
      if (!successors.has(e.predecessorId)) successors.set(e.predecessorId, []);
      successors.get(e.predecessorId)!.push(t.id!);
    }
  }

  const q: string[] = [];
  inDegree.forEach((deg, id) => {
    if (deg === 0) q.push(id);
  });
  const order: string[] = [];
  while (q.length) {
    const id = q.shift()!;
    order.push(id);
    for (const s of successors.get(id) || []) {
      const nd = (inDegree.get(s) || 0) - 1;
      inDegree.set(s, nd);
      if (nd === 0) q.push(s);
    }
  }
  if (order.length !== ids.length) {
    return { ok: false, error: 'Tasks have a circular dependency' };
  }
  return { ok: true, order };
}

/** Merge FS/SS → earliest start; FF/SF → earliest finish (from predecessor anchors + lag). */
function mergePredecessorBounds(
  edges: TaskDependencyEdge[],
  predState: Map<string, TaskDateState>,
): { needStart?: Date; needEnd?: Date } {
  let needStart: Date | undefined;
  let needEnd: Date | undefined;

  for (const e of edges) {
    const p = predState.get(e.predecessorId);
    if (!p) continue;
    const lag = Math.floor(Number(e.lagBusinessDays) || 0);
    switch (e.type) {
      case 'FS': {
        const ns = earliestSuccessorStart(p.endDate, lag);
        needStart = needStart === undefined || ns > needStart ? ns : needStart;
        break;
      }
      case 'SS': {
        const ns = earliestSuccessorStartSs(p.startDate, lag);
        needStart = needStart === undefined || ns > needStart ? ns : needStart;
        break;
      }
      case 'FF': {
        const ne = earliestSuccessorFinishFf(p.endDate, lag);
        needEnd = needEnd === undefined || ne > needEnd ? ne : needEnd;
        break;
      }
      case 'SF': {
        const ne = earliestSuccessorFinishSf(p.startDate, lag);
        needEnd = needEnd === undefined || ne > needEnd ? ne : needEnd;
        break;
      }
      default:
        break;
    }
  }
  return { needStart, needEnd };
}

/**
 * Satisfy mixed FS/SS start bounds and FF/SF finish bounds while preserving inclusive business-day duration.
 */
function convergeSuccessorDates(
  cur: TaskDateState,
  edges: TaskDependencyEdge[],
  predState: Map<string, TaskDateState>,
): TaskDateState {
  let start = stripTime(snapStartToBusinessDay(cur.startDate));
  let end = stripTime(snapEndToBusinessDay(cur.endDate));
  let dur = countBusinessDaysInclusive(start, end);

  for (let iter = 0; iter < 16; iter++) {
    const { needStart, needEnd } = mergePredecessorBounds(edges, predState);
    let changed = false;

    if (needStart !== undefined && start.getTime() < stripTime(needStart).getTime()) {
      start = stripTime(snapStartToBusinessDay(needStart));
      end = stripTime(endDateFromStartAndInclusiveBizDays(start, dur));
      changed = true;
    }
    if (needEnd !== undefined && end.getTime() < stripTime(needEnd).getTime()) {
      end = stripTime(snapEndToBusinessDay(needEnd));
      start = stripTime(startDateFromEndAndInclusiveBizDays(end, dur));
      changed = true;
    }
    dur = countBusinessDaysInclusive(start, end);

    if (needStart !== undefined && start.getTime() < stripTime(needStart).getTime()) {
      start = stripTime(snapStartToBusinessDay(needStart));
      end = stripTime(endDateFromStartAndInclusiveBizDays(start, dur));
      changed = true;
    }

    if (!changed) break;
  }

  return { startDate: start, endDate: end };
}

/** Enforce FS/SS/FF/SF constraints from predecessors; preserves inclusive business duration when possible. */
export function clampTaskToPredecessors(
  task: ProjectTask,
  tasksById: Map<string, ProjectTask>,
  proposedStart: Date,
  proposedEnd: Date,
): TaskDateState {
  let start = snapStartToBusinessDay(proposedStart);
  let end = snapEndToBusinessDay(proposedEnd);
  if (stripTime(end).getTime() < stripTime(start).getTime()) end = new Date(start);

  const edges = getDependencyEdges(task).filter((e) => tasksById.has(e.predecessorId));
  const predState = new Map<string, TaskDateState>();
  for (const e of edges) {
    const pred = tasksById.get(e.predecessorId);
    if (!pred?.id) continue;
    predState.set(e.predecessorId, {
      startDate: snapStartToBusinessDay(new Date(pred.startDate)),
      endDate: snapEndToBusinessDay(new Date(pred.endDate)),
    });
  }

  return convergeSuccessorDates({ startDate: start, endDate: end }, edges, predState);
}

/**
 * After setting `movedId` to new dates, ripple FS constraints forward (same project).
 * Returns map of task id → dates for all tasks whose dates changed (including moved).
 */
export function scheduleAfterMove(
  projectTasks: ProjectTask[],
  movedId: string,
  movedStart: Date,
  movedEnd: Date,
): ScheduleResult {
  const movedTask = projectTasks.find((t) => t.id === movedId);
  if (!movedTask?.projectId) {
    return { dates: new Map(), error: 'Task not found' };
  }
  if (isTaskLocked(movedTask)) {
    return { dates: new Map(), error: 'Task is locked. Unlock it before changing dates.' };
  }
  const projectId = movedTask.projectId;

  const inProject = projectTasks.filter((t) => t.projectId === projectId && t.id);
  const byId = new Map(inProject.map((t) => [t.id!, t]));

  const topo = topologicalOrder(inProject);
  if (!topo.ok) return { dates: new Map(), error: topo.error };

  const state = buildStateMap(inProject);
  const clamped = clampTaskToPredecessors(movedTask, byId, movedStart, movedEnd);
  state.set(movedId, clamped);

  const dbg = isGanttScheduleDebugEnabled();
  if (dbg) {
    ganttScheduleDebugGroup(`scheduleAfterMove · ${formatGanttTaskIdForLog(movedId)}`, () => {
      ganttScheduleDebugLog('requested (calendar)', { start: fmtDay(movedStart), end: fmtDay(movedEnd) });
      ganttScheduleDebugLog('after clamp to predecessors', {
        start: fmtDay(clamped.startDate),
        end: fmtDay(clamped.endDate),
      });
      ganttScheduleDebugLog('project task count', inProject.length, 'topo order length', topo.order?.length ?? 0);
    });
  }

  const order = topo.order!;
  const violations = new Map<string, ScheduleLockViolation>();

  for (let iter = 0; iter < Math.max(inProject.length * 4, 8); iter++) {
    let changed = false;
    for (const tid of order) {
      if (tid === movedId) continue;
      const t = byId.get(tid);
      if (!t?.id) continue;
      const cur = state.get(tid)!;
      const edges = getDependencyEdges(t).filter((e) => state.has(e.predecessorId));
      if (edges.length === 0) continue;

      const predState = new Map<string, TaskDateState>();
      for (const e of edges) {
        const ps = state.get(e.predecessorId);
        if (ps) predState.set(e.predecessorId, ps);
      }
      const { needStart, needEnd } = mergePredecessorBounds(edges, predState);
      const startBad = needStart !== undefined && stripTime(cur.startDate).getTime() < stripTime(needStart).getTime();
      const endBad = needEnd !== undefined && stripTime(cur.endDate).getTime() < stripTime(needEnd).getTime();
      const satisfied = !startBad && !endBad;
      if (satisfied) continue;

      // Locked tasks stay pinned; record a violation so the PM knows links are broken until they intervene.
      if (isTaskLocked(t)) {
        if (!violations.has(tid)) {
          violations.set(tid, {
            id: tid,
            pinnedReason: lockViolationPinnedReason(t),
            ...(startBad ? { requiredStart: stripTime(needStart!), currentStart: stripTime(cur.startDate) } : {}),
            ...(endBad ? { requiredEnd: stripTime(needEnd!), currentEnd: stripTime(cur.endDate) } : {}),
          });
        }
        continue;
      }

      const next = convergeSuccessorDates(cur, edges, predState);
      if (
        stripTime(next.startDate).getTime() === stripTime(cur.startDate).getTime() &&
        stripTime(next.endDate).getTime() === stripTime(cur.endDate).getTime()
      ) {
        continue;
      }
      if (dbg) {
        ganttScheduleDebugLog(`ripple iter ${iter} · ${formatGanttTaskIdForLog(tid)}`, {
          from: { start: fmtDay(cur.startDate), end: fmtDay(cur.endDate) },
          to: { start: fmtDay(next.startDate), end: fmtDay(next.endDate) },
          needStartFmt: needStart ? fmtDay(needStart) : undefined,
          needEndFmt: needEnd ? fmtDay(needEnd) : undefined,
          edges: getDependencyEdges(t).map((e) => ({
            pred: e.predecessorId.slice(0, 8) + '…',
            type: e.type,
            lag: e.lagBusinessDays,
          })),
        });
      }
      state.set(tid, next);
      changed = true;
    }
    if (!changed) break;
  }

  if (dbg) {
    ganttScheduleDebugLog('scheduleAfterMove final rows', inProject.length);
  }

  return { dates: state, violations: Array.from(violations.values()) };
}

/**
 * Recompute FS lag on each successor’s edges so stored lag matches final dates (after drag/ripple).
 */
export function reinferFsLagsForDates(
  tasks: ProjectTask[],
  dates: Map<string, TaskDateState>,
): Map<string, TaskDependencyEdge[]> {
  const out = new Map<string, TaskDependencyEdge[]>();
  for (const t of tasks) {
    if (!t.id) continue;
    const edges = getDependencyEdges(t);
    if (!edges.length) continue;
    const succState = dates.get(t.id);
    if (!succState) continue;
    let any = false;
    const newEdges = edges.map((e) => {
      const pState = dates.get(e.predecessorId);
      if (!pState) return e;
      let lag: number;
      switch (e.type) {
        case 'SS':
          lag = inferSsLagBusinessDays(pState.startDate, succState.startDate);
          break;
        case 'FF':
          lag = inferFfLagBusinessDays(pState.endDate, succState.endDate);
          break;
        case 'SF':
          lag = inferSfLagBusinessDays(pState.startDate, succState.endDate);
          break;
        default:
          lag = inferFsLagBusinessDays(pState.endDate, succState.startDate);
          break;
      }
      const clamped = Math.max(-500, Math.min(500, lag));
      if (clamped !== normalizeLag(e.lagBusinessDays)) any = true;
      return { ...e, type: e.type, lagBusinessDays: clamped };
    });
    if (any) out.set(t.id, newEdges);
  }
  return out;
}

function normalizeLag(raw: unknown): number {
  return Math.max(-500, Math.min(500, Math.floor(Number(raw) || 0)));
}

/**
 * Recompute all task dates in a project to satisfy FS edges (business days).
 * Use after adding/removing dependencies without a bar drag.
 */
export function enforceAllFsDependencies(projectTasks: ProjectTask[]): ScheduleResult {
  const first = projectTasks.find((t) => t.id);
  if (!first?.projectId) return { dates: new Map() };
  const projectId = first.projectId;

  const inProject = projectTasks.filter((t) => t.projectId === projectId && t.id);
  if (inProject.length === 0) return { dates: new Map() };

  const byId = new Map(inProject.map((t) => [t.id!, t]));
  const topo = topologicalOrder(inProject);
  if (!topo.ok) return { dates: new Map(), error: topo.error };

  const state = buildStateMap(inProject);
  const order = topo.order!;

  const dbgEnforce = isGanttScheduleDebugEnabled();
  if (dbgEnforce) {
    ganttScheduleDebugGroup(`enforceAllFsDependencies · project ${projectId.slice(0, 8)}…`, () => {
      ganttScheduleDebugLog('tasks in graph', inProject.length);
    });
  }

  const violations = new Map<string, ScheduleLockViolation>();

  for (let iter = 0; iter < Math.max(inProject.length * 4, 8); iter++) {
    let changed = false;
    for (const tid of order) {
      const t = byId.get(tid);
      if (!t?.id) continue;
      const cur = state.get(tid)!;
      const edges = getDependencyEdges(t).filter((e) => state.has(e.predecessorId));
      if (edges.length === 0) continue;

      const predState = new Map<string, TaskDateState>();
      for (const e of edges) {
        const ps = state.get(e.predecessorId);
        if (ps) predState.set(e.predecessorId, ps);
      }
      const { needStart, needEnd } = mergePredecessorBounds(edges, predState);
      const startBad = needStart !== undefined && stripTime(cur.startDate).getTime() < stripTime(needStart).getTime();
      const endBad = needEnd !== undefined && stripTime(cur.endDate).getTime() < stripTime(needEnd).getTime();
      const satisfied = !startBad && !endBad;
      if (satisfied) continue;

      if (isTaskLocked(t)) {
        if (!violations.has(tid)) {
          violations.set(tid, {
            id: tid,
            pinnedReason: lockViolationPinnedReason(t),
            ...(startBad ? { requiredStart: stripTime(needStart!), currentStart: stripTime(cur.startDate) } : {}),
            ...(endBad ? { requiredEnd: stripTime(needEnd!), currentEnd: stripTime(cur.endDate) } : {}),
          });
        }
        continue;
      }

      const next = convergeSuccessorDates(cur, edges, predState);
      if (
        stripTime(next.startDate).getTime() === stripTime(cur.startDate).getTime() &&
        stripTime(next.endDate).getTime() === stripTime(cur.endDate).getTime()
      ) {
        continue;
      }
      if (dbgEnforce) {
        ganttScheduleDebugLog(`enforce iter ${iter} · ${formatGanttTaskIdForLog(tid)}`, {
          from: { start: fmtDay(cur.startDate), end: fmtDay(cur.endDate) },
          to: { start: fmtDay(next.startDate), end: fmtDay(next.endDate) },
          edges: getDependencyEdges(t).map((e) => ({
            pred: e.predecessorId.slice(0, 8) + '…',
            type: e.type,
            lag: e.lagBusinessDays,
          })),
        });
      }
      state.set(tid, next);
      changed = true;
    }
    if (!changed) break;
  }

  return { dates: state, violations: Array.from(violations.values()) };
}

/**
 * Apply the same **calendar-day** delta to the moved task’s start (derived from requested vs previous start)
 * to every task in the undirected dependency component (linked through FS/SS edges). Milestone pseudo-tasks
 * in that component move with the same delta so links stay aligned.
 */
export function scheduleShiftConnectedComponent(
  projectTasks: ProjectTask[],
  movedId: string,
  movedStart: Date,
  movedEnd: Date,
): ScheduleResult {
  const movedTask = projectTasks.find((t) => t.id === movedId);
  if (!movedTask?.projectId) {
    return { dates: new Map(), error: 'Task not found' };
  }
  if (isTaskLocked(movedTask)) {
    return { dates: new Map(), error: 'Task is locked. Unlock it before changing dates.' };
  }
  const projectId = movedTask.projectId;
  const inProject = projectTasks.filter((t) => t.projectId === projectId && t.id);
  const byId = new Map(inProject.map((t) => [t.id!, t]));
  const topo = topologicalOrder(inProject);
  if (!topo.ok) return { dates: new Map(), error: topo.error };

  const componentIds = taskIdsInUndirectedDependencyComponent(inProject, projectId, movedId);
  if (componentIds.size === 0) {
    return { dates: new Map(), error: 'Task not in dependency graph' };
  }

  const state = buildStateMap(inProject);
  const prevMoved = state.get(movedId);
  if (!prevMoved) return { dates: new Map(), error: 'Task state missing' };

  const deltaMs = stripTime(movedStart).getTime() - stripTime(prevMoved.startDate).getTime();
  const deltaDays = Math.round(deltaMs / 86400000);

  const violations = new Map<string, ScheduleLockViolation>();

  for (const id of Array.from(componentIds)) {
    const cur = state.get(id);
    if (!cur) continue;
    const t = byId.get(id);

    // Locked tasks (including completed) stay pinned during a chain shift; record the
    // would-be start so the UI can show the link conflict.
    if (id !== movedId && t && isTaskLocked(t)) {
      const ns = new Date(cur.startDate);
      ns.setDate(ns.getDate() + deltaDays);
      violations.set(id, {
        id,
        pinnedReason: lockViolationPinnedReason(t),
        requiredStart: snapStartToBusinessDay(ns),
        currentStart: stripTime(cur.startDate),
      });
      continue;
    }

    const ns = new Date(cur.startDate);
    ns.setDate(ns.getDate() + deltaDays);
    const ne = new Date(cur.endDate);
    ne.setDate(ne.getDate() + deltaDays);
    state.set(id, {
      startDate: snapStartToBusinessDay(ns),
      endDate: snapEndToBusinessDay(ne),
    });
  }

  return { dates: state, violations: Array.from(violations.values()) };
}

/** Which task ids changed vs original tasks (by reference dates). */
export function diffDateChanges(
  originalTasks: ProjectTask[],
  newDates: Map<string, TaskDateState>,
): { id: string; startDate: Date; endDate: Date }[] {
  const byId = new Map(originalTasks.filter((t) => t.id).map((t) => [t.id!, t]));
  const out: { id: string; startDate: Date; endDate: Date }[] = [];
  newDates.forEach((dates, id) => {
    const orig = byId.get(id);
    if (!orig) return;
    const os = stripTime(new Date(orig.startDate)).getTime();
    const oe = stripTime(new Date(orig.endDate)).getTime();
    const ns = stripTime(dates.startDate).getTime();
    const ne = stripTime(dates.endDate).getTime();
    if (os !== ns || oe !== ne) {
      out.push({ id, startDate: dates.startDate, endDate: dates.endDate });
    }
  });
  return out;
}

/** Pairs of overlapping tasks (any projects) for CNC-style warnings. */
export function findOverlappingTaskPairs(
  tasks: ProjectTask[],
  predicate: (t: ProjectTask) => boolean,
): Array<{ a: ProjectTask; b: ProjectTask }> {
  const list = tasks.filter((t) => t.id && predicate(t));
  const pairs: Array<{ a: ProjectTask; b: ProjectTask }> = [];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i];
      const b = list[j];
      const as = stripTime(new Date(a.startDate)).getTime();
      const ae = stripTime(new Date(a.endDate)).getTime();
      const bs = stripTime(new Date(b.startDate)).getTime();
      const be = stripTime(new Date(b.endDate)).getTime();
      if (as <= be && bs <= ae) {
        pairs.push({ a, b });
      }
    }
  }
  return pairs;
}
